import { useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Button, CircularProgress, Grid, Box } from '@mui/material'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { getAttributeList } from './cognito-atributes'
import { InputTupple } from './input-tuple'
import { useStyles } from './style'
import { samlAttrToCognitoAttr } from './saml-attr-map'
import { BannerStatic } from '../../../../components'
import { useTenantFormContext } from '../../tenant-form-context'

const SamlAttributeInput = React.memo(React.forwardRef((props, ref) => {
  const { provider, setValue, attributeMapList, disableBtnAutofill, getCustomAttributeList, isLoading } = props
  const { tenant } = useTenantFormContext()
  const { userPoolId } = tenant
  const classes = useStyles()
  React.useImperativeHandle(ref, () => ({
    fillAttrMetadata: updateValuesFromMetadata
  }))

  const attributeList = React.useMemo(() => getAttributeList(), [])

  const [attributes, setAttributes] = React.useState([])

  useEventsOnViewLoad(() => {
    getCustomAttributeList.registerAdhocOnSuccessListener(({ payload }) => {
      payload.forEach(attr => {
        const existingAttr = attributeList.find(a => a.name === attr.name)
        if (!existingAttr) {
          attributeList.push(attr)
        }
      })
    })
  }, [getCustomAttributeList])

  React.useEffect(() => {
    if (!provider) return
    const ssoAttributes = provider.attributeMapping
    if (ssoAttributes?.length > 0) {
      setAttributes(ssoAttributes)
      attributeList.forEach(attr => {
        attr.disabled = !!ssoAttributes.find(ssoAttr => attr.name === ssoAttr.name)
      })
    }
  }, [provider, attributeList])

  React.useEffect(() => {
    getCustomAttributeList.trigger({ userPoolId })
  }, [getCustomAttributeList, userPoolId])

  const addAttribute = React.useCallback(() => {
    setAttributes(state => {
      const lastAttribute = state[(state.length - 1)]
      if (lastAttribute && !lastAttribute?.name) return [...state]
      return [...state, { key: uuidv4() }]
    })
  }, [setAttributes])

  const removeAttribute = React.useCallback((index) => {
    setAttributes(state => {
      const deletedAttr = state[index]
      if (deletedAttr) {
        const updatedState = state.filter((_, i) => i !== index)
        const attr = attributeList.find(attr => attr.name === deletedAttr.name)
        if (attr) attr.disabled = false
        setValue('attributeMapping', updatedState)
        return updatedState
      }
      return state
    })
  }, [setAttributes, setValue, attributeList])

  const onChangeAttributes = React.useCallback((index, { name, value, oldName }) => {
    setAttributes(state => {
      const newState = [...state]
      newState[index] = { ...newState[index], [name]: value }
      setValue('attributeMapping', newState)
      if (name === 'name') attributeList.find(attr => attr.name === value).disabled = true
      if (oldName) attributeList.find(attr => attr.name === oldName).disabled = false
      return newState
    })
  }, [setAttributes, setValue, attributeList])

  // Updating attrMap form values(user pool attribute and attribute name) from SAML metadata xml
  const updateValuesFromMetadata = () => {
    if (attributeMapList.length > 0) {
      const newAttributeList = attributeMapList.map((attr, index) => {
        const cognitoSetName = samlAttrToCognitoAttr(attr)
        return {
          value: attr,
          name: cognitoSetName?.cognitoAttr?.name,
          key: index
        }
      })
      setAttributes(newAttributeList)
      BannerStatic.show({ label: 'Attribute Mapping successfully populated.' })
    } else {
      BannerStatic.show({ label: 'Couldn’t find the attribute mapping inside the SAML Metadata', isError: true })
    }
  }

  return (
    <>
      <Grid container direction='column' className={classes.root}>
        {isLoading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {
              attributes.map((attr, index) => (
                <InputTupple
                  key={attr.key}
                  attribute={attr}
                  attributeList={attributeList}
                  attributeMapList={attributeMapList}
                  onRemove={() => removeAttribute(index)}
                  onChange={(ev) => onChangeAttributes(index, ev)}
                />
              ))
            }

            <Grid style={{ fontSize: 14 }}>
              <Button
                variant='outlined'
                data-test='add-attribute-button'
                onClick={addAttribute}
                className={classes.addAttrBtn}
              >
                Add Attribute
              </Button>
              {!attributes.some(attr => !!attr.name) ? (
                <>
                  Or
                  <Button
                    variant='contained'
                    color='primary'
                    data-test='set-attribute-from-meta'
                    onClick={updateValuesFromMetadata}
                    className={classes.autofillBtn}
                    disabled={disableBtnAutofill}
                  >
                    Autofill from SAML Metadata
                  </Button>
                </>
              ) : null}
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}))

export { SamlAttributeInput }
